<template>
  <div class="flex-grow flex flex-col w-full relative">
    <SnapshotsArea
      :snapshots="snapshots"
      :clinicianName="clinicianName"
      :user-id="$page?.props?.user?.id"
      @removeSnapshot="removeSnapshot($event)"
    />
  </div>
</template>

<script>
import { computed, onUnmounted, ref, watch } from 'vue';
import useSessionStore from '@/Stores/useSessionStore';
import handleAsyncRequest from '@/Utils/handleAsyncRequest';
import SnapshotsArea from '@/Components/Chat/SnapshotsArea.vue';

export default {
  props: {
    clinicianName: {
      type: String,
    },
  },
  components: { SnapshotsArea },
  setup() {
    const autoFetch = ref(null);
    const sessionStore = useSessionStore();
    const snapshots = computed(() => sessionStore.snapshots || []);
    const sessionId = computed(() => sessionStore.sessionDBId);

    const getSnapshots = async () => {
      let imageSnapshots = [];
      const [res, err] = await handleAsyncRequest(fetchSnapshots());
      if (!err) {
        imageSnapshots = res.snapshots || [];
      } else {
        console.error('Error fetching snapshot:', err);
      }
      // Fetch video snapshots if available
      const [videoRes, videoErr] = await handleAsyncRequest(fetchVideoSnapshots());
      if (!videoErr) {
        const videoSnapshots = videoRes.recordings || [];
        // Merge new snapshots and update existing ones when status changed
        const existingList = sessionStore.snapshots || [];
        const existingMap = new Map(existingList.map((s) => [s.id, s]));
        const fetchedAll = [...imageSnapshots, ...videoSnapshots];

        const itemsToAdd = fetchedAll.filter((s) => !existingMap.has(s.id));
        const itemsToUpdate = fetchedAll.filter((s) => {
          const existing = existingMap.get(s.id);
          return existing && existing.status !== s.status;
        });

        let nextSnapshots = existingList;

        if (itemsToUpdate.length) {
          const updateMap = new Map(itemsToUpdate.map((s) => [s.id, s]));
          nextSnapshots = nextSnapshots.map((item) => updateMap.get(item.id) || item);
        }

        if (itemsToAdd.length) {
          nextSnapshots = [...nextSnapshots, ...itemsToAdd];
        }

        if (itemsToAdd.length || itemsToUpdate.length) {
          sessionStore.snapshots = nextSnapshots.sort(
            (a, b) => new Date(a.captured_at) - new Date(b.captured_at),
          );
        }
      } else {
        console.error('Error fetching snapshot:', videoErr);
      }
    };

    const fetchSnapshots = async () => {
      try {
        const res = await fetch(route('snapshot.get', { session: sessionId.value }));
        return res.json();
      } catch (error) {
        throw error.response;
      }
    };

    const fetchVideoSnapshots = async () => {
      try {
        const res = await fetch(route('recording.session.get', { session: sessionId.value }));
        return res.json();
      } catch (error) {
        throw error.response;
      }
    };

    const removeSnapshot = async (data) => {
      const { snapshotId, type } = data;
      if (type === 'video') {
        // Video snapshot removal
        const [res, err] = await handleAsyncRequest(deleteVideoSnapshot(snapshotId));
        if (!err && res?.success) {
          sessionStore.snapshots = sessionStore.snapshots.filter(
            (snapshot) => snapshot.id !== snapshotId,
          );
        } else {
          console.error('Error of removing video snapshot:', err);
        }
      } else {
        // Image snapshot removal
        const [res, err] = await handleAsyncRequest(deleteImageSnapshot(snapshotId));
        if (!err && res.success) {
          sessionStore.snapshots = sessionStore.snapshots.filter(
            (snapshot) => snapshot.id !== snapshotId,
          );
        } else {
          console.error('Error of removing snapshot:', res.status);
        }
      }
    };

    const deleteImageSnapshot = async (snapshotId) => {
      try {
        const formData = new FormData();
        formData.append('snapshot', snapshotId);

        const res = await fetch(route('snapshot.delete'), {
          method: 'POST',
          body: formData,
        });
        return res.json();
      } catch (error) {
        throw error.response;
      }
    };

    const deleteVideoSnapshot = async (recordingId) => {
      try {
        const res = await fetch(route('recording.delete', { recordingId }), {
          method: 'DELETE',
        });
        return res.json();
      } catch (error) {
        throw error.response;
      }
    };

    watch(sessionId, (newVal) => {
      if (newVal && sessionStore.isClinician) {
        getSnapshots();
        if (autoFetch.value === null) {
          autoFetch.value = setInterval(async () => {
            // Update snapshot pool
            await getSnapshots();
          }, 15000);
        }
      }
    });

    onUnmounted(() => {
      if (autoFetch.value) {
        clearInterval(autoFetch.value);
        autoFetch.value = null;
      }
    });

    return {
      autoFetch,
      snapshots,
      getSnapshots,
      removeSnapshot,
    };
  },
};
</script>
